// 底版「人工标框」工具 — /content/:id/plates
//
// 运营在某套系的干净底版图上用鼠标拖拽画出每个人脸位置的框 (归一化 bbox 0..1)，
// 给每个框编号 (order) 与可选 role，保存为 faces 元数据，驱动按框逐框单脸换脸 +
// 前端上传引导。底版图本身保持干净 (框只是元数据，不烤进图)。
//
// 交互:
//  - 左侧列出该 package 的全部 plates (缩略 + 框数 + 状态)，点击选中。
//  - 右侧大图叠加可拖拽画框层: 在空白处按下拖动 → 新框; 已存框可选中后删除/改 order/role。
//  - 「保存」调 PUT 存 faces; 可顺带把 draft → active。

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Space,
  Button,
  Skeleton,
  Alert,
  Tag,
  Table,
  InputNumber,
  Input,
  Select,
  Modal,
  Popconfirm,
  Typography,
  Collapse,
  Empty,
  Spin,
  message,
  Image as AntImage,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
  CopyOutlined,
  SwapOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  getPackagePlates,
  savePlateFaces,
  importPlatesFromSamples,
  triggerPlateAutogen,
  listPlateRuns,
  listRunCandidates,
  selectCandidate,
  type Plate,
  type PlateFace,
  type PlateAutogenEngine,
  type PlateRun,
  reassignPlate,
  deleteFailedRun,
} from '@/api/plates';
import { getPackage, type ContentPackage } from '@/api/content';

const { Text } = Typography;

/** 当前正在绘制的框 (归一化坐标)。 */
interface DraftRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

const BOX_COLORS = ['#4f46e5', '#16a34a', '#ea580c', '#db2777', '#0891b2', '#ca8a04'];

function colorFor(idx: number): string {
  return BOX_COLORS[idx % BOX_COLORS.length];
}

/** 把任意两点构成的矩形归一并裁剪到 [0,1]。 */
function normalizeRect(x0: number, y0: number, x1: number, y1: number): DraftRect {
  const x = Math.max(0, Math.min(x0, x1));
  const y = Math.max(0, Math.min(y0, y1));
  const w = Math.min(1, Math.max(x0, x1)) - x;
  const h = Math.min(1, Math.max(y0, y1)) - y;
  return { x, y, w, h };
}

export function ContentPlatesPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const packageId = Number(id);

  const [pkg, setPkg] = useState<ContentPackage | null>(null);
  const [plates, setPlates] = useState<Plate[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  // 「后台生成底版」弹窗状态。
  const [autogenOpen, setAutogenOpen] = useState(false);
  const [autogenSubmitting, setAutogenSubmitting] = useState(false);
  const [agPrompt, setAgPrompt] = useState('');
  const [agEngine, setAgEngine] = useState<PlateAutogenEngine>('jimeng');
  const [agCount, setAgCount] = useState(4);

  // 「生成记录」区 (plate-generation-runs): 批次列表 + 按批次懒加载候选。
  const [runs, setRuns] = useState<PlateRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [candByRun, setCandByRun] = useState<Record<number, Plate[]>>({});
  const [candLoading, setCandLoading] = useState<Record<number, boolean>>({});
  const [selectingId, setSelectingId] = useState<number | null>(null);

  // 选中的 plate 与其编辑中的 faces (本地草稿，保存前不回写服务端)。
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [faces, setFaces] = useState<PlateFace[]>([]);
  const [activeFace, setActiveFace] = useState<number | null>(null); // 选中框的数组下标
  const [dirty, setDirty] = useState(false);

  // 拖拽绘制状态。
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const [drawing, setDrawing] = useState<DraftRect | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  const selectedPlate = plates.find((p) => p.plate_id === selectedId) ?? null;

  const load = useCallback(() => {
    if (!packageId) return;
    setLoading(true);
    Promise.all([getPackage(packageId), getPackagePlates(packageId)])
      .then(([p, ps]) => {
        setPkg(p);
        setPlates(ps);
        // 默认选中第一张。
        if (ps.length > 0) {
          setSelectedId((cur) => (cur && ps.some((x) => x.plate_id === cur) ? cur : ps[0].plate_id));
        }
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [packageId]);

  // 拉「生成记录」批次列表 (plate-generation-runs)。
  const loadRuns = useCallback(() => {
    if (!packageId) return;
    setRunsLoading(true);
    listPlateRuns(packageId)
      .then((rs) => setRuns(rs))
      .catch((e) => message.error(e instanceof Error ? e.message : '加载生成记录失败'))
      .finally(() => setRunsLoading(false));
  }, [packageId]);

  // 展开某批次 → 懒加载其候选缩略图。
  const loadCandidates = useCallback(
    (runId: number) => {
      if (!packageId) return;
      setCandLoading((m) => ({ ...m, [runId]: true }));
      listRunCandidates(packageId, runId)
        .then((cs) => setCandByRun((m) => ({ ...m, [runId]: cs })))
        .catch((e) => message.error(e instanceof Error ? e.message : '加载候选失败'))
        .finally(() => setCandLoading((m) => ({ ...m, [runId]: false })));
    },
    [packageId],
  );

  const onRunsCollapseChange = useCallback(
    (keys: string | string[]) => {
      const arr = Array.isArray(keys) ? keys : [keys];
      // 新展开且未加载过候选的批次 → 拉取。
      arr.forEach((k) => {
        const runId = Number(k);
        if (runId && candByRun[runId] === undefined) loadCandidates(runId);
      });
    },
    [candByRun, loadCandidates],
  );

  // 「选入套系」: candidate→draft, 从候选列表移除 + 刷新标框工作区。
  const handleSelectCandidate = useCallback(
    (runId: number, plateId: number) => {
      if (!packageId) return;
      setSelectingId(plateId);
      selectCandidate(packageId, plateId)
        .then(() => {
          setCandByRun((m) => ({
            ...m,
            [runId]: (m[runId] ?? []).filter((c) => c.plate_id !== plateId),
          }));
          message.success('已选入套系, 请在下方标框工作区标框');
          load(); // 刷新标框工作区 (新 draft 出现)
        })
        .catch((e) => message.error(e instanceof Error ? e.message : '选入失败'))
        .finally(() => setSelectingId(null));
    },
    [packageId, load],
  );

  // 删除(归档)候选: 复用 PUT status=archived, 从候选列表移除。
  const handleDeleteCandidate = useCallback(
    (runId: number, cand: Plate) => {
      if (!packageId) return;
      savePlateFaces(packageId, cand.plate_id, cand.faces, 'archived')
        .then(() => {
          setCandByRun((m) => ({
            ...m,
            [runId]: (m[runId] ?? []).filter((c) => c.plate_id !== cand.plate_id),
          }));
          message.success('已删除该候选');
        })
        .catch((e) => message.error(e instanceof Error ? e.message : '删除失败'));
    },
    [packageId],
  );

  // 从套系现有底版 (package_samples, 创想熊导入的) 导入为 plate, 供标框。
  const handleImportFromSamples = useCallback(() => {
    if (!packageId) return;
    setImporting(true);
    importPlatesFromSamples(packageId)
      .then((ps) => {
        setPlates(ps);
        if (ps.length > 0) setSelectedId(ps[0].plate_id);
        message.success(ps.length > 0 ? `已导入 ${ps.length} 张底版，可开始标框` : '套系暂无底版可导入');
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '导入失败'))
      .finally(() => setImporting(false));
  }, [packageId]);

  // 触发「后台生成底版」: 起 WF_PLATE_AUTOGEN 异步生成 N 张干净底版落 draft。
  const handleAutogen = useCallback(() => {
    if (!packageId) return;
    const prompt = agPrompt.trim();
    if (!prompt) {
      message.warning('请填写底版画面 prompt');
      return;
    }
    setAutogenSubmitting(true);
    triggerPlateAutogen(packageId, { prompt, engine: agEngine, count: agCount })
      .then((r) => {
        setAutogenOpen(false);
        message.success(
          `已提交生成 ${r.count} 张底版 (引擎 ${r.engine})，候选将落到上方「生成记录」批次 #${r.run_id}，生成完成后展开挑选`,
        );
        loadRuns(); // 立即刷新, 新批次 (running) 出现在生成记录区
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '提交失败'))
      .finally(() => setAutogenSubmitting(false));
  }, [packageId, agPrompt, agEngine, agCount, loadRuns]);

  // 转移候选到其他套系 (reassignPlate; 后端校验同 App + candidate)
  const [transferPlate, setTransferPlate] = useState<{ runId: number; plateId: number } | null>(null);
  const [transferTarget, setTransferTarget] = useState<number | undefined>(undefined);
  const [pkgOptions, setPkgOptions] = useState<{ value: number; label: string }[]>([]);
  const [transferring, setTransferring] = useState(false);
  const [pkgSearching, setPkgSearching] = useState(false);
  const pkgSearchTimer = useRef<ReturnType<typeof setTimeout>>();
  const openTransfer = (runId: number, plateId: number) => {
    setTransferPlate({ runId, plateId });
    setTransferTarget(undefined);
    if (pkgOptions.length === 0 && pkg?.app_id) {
      listPackagesForTransfer(pkg.app_id, '');
    }
  };
  // 服务端搜索: App 下套系可达数千个(cxx 批量导入), 只拉一页做客户端过滤
  // 搜不到排名靠后的套系 (2026-08-09 "搜索不到中秋" 根因) → q 参数走 ILIKE。
  const listPackagesForTransfer = (appId: number, q: string) => {
    setPkgSearching(true);
    import('@/api/content').then(({ listPackages }) =>
      listPackages({ app_id: appId, q: q || undefined, page_size: 50 })
        .then((r) => setPkgOptions(r.data.filter((x: any) => x.id !== packageId).map((x: any) => ({ value: x.id, label: `#${x.id} ${x.name}` }))))
        .catch(() => message.error('加载套系列表失败'))
        .finally(() => setPkgSearching(false))
    );
  };
  const onPkgSearch = (q: string) => {
    if (!pkg?.app_id) return;
    if (pkgSearchTimer.current) clearTimeout(pkgSearchTimer.current);
    pkgSearchTimer.current = setTimeout(() => listPackagesForTransfer(pkg.app_id!, q.trim()), 300);
  };
  const doTransfer = () => {
    if (!transferPlate || !transferTarget) return;
    setTransferring(true);
    reassignPlate(transferPlate.plateId, transferTarget)
      .then(() => {
        message.success('已转移(仍为候选)。到「内容生产→照片库」筛该套系点「选入」');
        setCandByRun((m) => ({ ...m, [transferPlate.runId]: (m[transferPlate.runId] || []).filter((c) => c.plate_id !== transferPlate.plateId) }));
        setTransferPlate(null);
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '转移失败'))
      .finally(() => setTransferring(false));
  };
  // 删除失败批次
  const handleDeleteRun = (runId: number) => {
    deleteFailedRun(packageId, runId)
      .then(() => {
        message.success('已删除失败批次');
        setRuns((rs) => rs.filter((r) => r.run_id !== runId));
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '删除失败'));
  };
  const copyPrompt = (prompt?: string) => {
    if (!prompt) { message.info('该批次无 prompt'); return; }
    navigator.clipboard.writeText(prompt).then(
      () => message.success('prompt 已复制'),
      () => message.error('复制失败')
    );
  };

  // 删除(归档)不满意的底版: PUT status=archived → 列表查询 status!='archived'
  // 自动隐藏。软删(保留 OSS 图 + 行), 不影响已用于订单的底版审计。
  const handleArchive = useCallback(
    (plateId: number, faces: PlateFace[]) => {
      if (!packageId) return;
      savePlateFaces(packageId, plateId, faces, 'archived')
        .then(() => {
          setPlates((prev) => prev.filter((p) => p.plate_id !== plateId));
          setSelectedId((cur) => (cur === plateId ? null : cur));
          message.success('已删除该底版');
        })
        .catch((e) => message.error(e instanceof Error ? e.message : '删除失败'));
    },
    [packageId],
  );

  useEffect(() => {
    load();
    loadRuns();
  }, [load, loadRuns]);

  // 切换选中 plate → 重置编辑草稿。
  useEffect(() => {
    if (!selectedPlate) {
      setFaces([]);
      setActiveFace(null);
      setDirty(false);
      return;
    }
    // 深拷贝，避免直接改到 plates 里的引用。
    setFaces(selectedPlate.faces.map((f) => ({ ...f, bbox: { ...f.bbox } })));
    setActiveFace(null);
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const selectPlate = (pid: number) => {
    if (dirty && !window.confirm('当前底版有未保存的标框，切换将丢弃，确认?')) return;
    setSelectedId(pid);
  };

  // --- 坐标换算: 鼠标事件 → 容器内归一化坐标 -------------------------------
  const toNorm = (clientX: number, clientY: number): { x: number; y: number } => {
    const el = imgWrapRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  };

  const onMouseDown = (e: React.MouseEvent) => {
    if (!selectedPlate) return;
    // 只响应主键，且点的是图层本身 (不是已存框上的删除按钮)。
    if (e.button !== 0) return;
    const p = toNorm(e.clientX, e.clientY);
    dragStart.current = p;
    setDrawing({ x: p.x, y: p.y, w: 0, h: 0 });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current) return;
    const p = toNorm(e.clientX, e.clientY);
    setDrawing(normalizeRect(dragStart.current.x, dragStart.current.y, p.x, p.y));
  };

  const onMouseUp = () => {
    const rect = drawing;
    dragStart.current = null;
    setDrawing(null);
    if (!rect) return;
    // 忽略过小的误触 (< 2% 宽或高)。
    if (rect.w < 0.02 || rect.h < 0.02) return;
    const nextOrder = (faces.reduce((m, f) => Math.max(m, f.order), 0) || 0) + 1;
    const newFace: PlateFace = { order: nextOrder, bbox: rect, role: '', gender: '' };
    setFaces((prev) => [...prev, newFace]);
    setActiveFace(faces.length);
    setDirty(true);
  };

  const updateFace = (idx: number, patch: Partial<PlateFace>) => {
    setFaces((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
    setDirty(true);
  };

  const removeFace = (idx: number) => {
    setFaces((prev) => prev.filter((_, i) => i !== idx));
    setActiveFace(null);
    setDirty(true);
  };

  const onSave = async (alsoActivate?: boolean) => {
    if (!selectedPlate) return;
    // order 唯一性校验。
    const orders = faces.map((f) => f.order);
    if (new Set(orders).size !== orders.length) {
      message.error('框的 order 不能重复');
      return;
    }
    setSaving(true);
    try {
      const updated = await savePlateFaces(
        packageId,
        selectedPlate.plate_id,
        faces,
        alsoActivate ? 'active' : undefined,
      );
      message.success('已保存标框');
      setDirty(false);
      // 用返回值更新本地 plates。
      setPlates((prev) =>
        prev.map((p) => (p.plate_id === updated.plate_id ? updated : p)),
      );
    } catch (e) {
      message.error(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // --- 渲染 -----------------------------------------------------------------
  const renderOverlay = () => {
    if (!selectedPlate) return null;
    return (
      <div
        ref={imgWrapRef}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{
          position: 'relative',
          display: 'inline-block',
          maxWidth: '100%',
          userSelect: 'none',
          cursor: 'crosshair',
          lineHeight: 0,
        }}
      >
        <img
          src={selectedPlate.plate_image_url}
          alt="plate"
          draggable={false}
          style={{ maxWidth: '100%', maxHeight: 620, display: 'block' }}
        />
        {/* 已存框 */}
        {faces.map((f, i) => {
          const c = colorFor(f.order - 1);
          const active = activeFace === i;
          return (
            <div
              key={i}
              onMouseDown={(e) => {
                e.stopPropagation();
                setActiveFace(i);
              }}
              style={{
                position: 'absolute',
                left: `${f.bbox.x * 100}%`,
                top: `${f.bbox.y * 100}%`,
                width: `${f.bbox.w * 100}%`,
                height: `${f.bbox.h * 100}%`,
                border: `2px solid ${c}`,
                boxShadow: active ? `0 0 0 2px ${c}55` : undefined,
                background: `${c}1a`,
                cursor: 'pointer',
                boxSizing: 'border-box',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  background: c,
                  color: '#fff',
                  fontSize: 12,
                  lineHeight: '16px',
                  padding: '0 5px',
                  borderRadius: '0 0 4px 0',
                }}
              >
                {f.order}
                {f.role ? ` ${f.role}` : ''}
              </span>
            </div>
          );
        })}
        {/* 正在拖拽的框 */}
        {drawing && (
          <div
            style={{
              position: 'absolute',
              left: `${drawing.x * 100}%`,
              top: `${drawing.y * 100}%`,
              width: `${drawing.w * 100}%`,
              height: `${drawing.h * 100}%`,
              border: '2px dashed #4f46e5',
              background: '#4f46e51a',
              pointerEvents: 'none',
              boxSizing: 'border-box',
            }}
          />
        )}
      </div>
    );
  };

  // 引擎标签色。
  const engineTag = (engine: string) => {
    if (engine === 'jimeng') return <Tag color="purple">即梦</Tag>;
    if (engine === 'onething') return <Tag color="geekblue">onething</Tag>;
    return <Tag>默认链</Tag>;
  };

  const runStatusTag = (r: PlateRun) => {
    if (r.status === 'running') return <Tag color="processing">生成中</Tag>;
    if (r.status === 'failed')
      return <Tag color="error">失败{r.error ? `: ${r.error}` : ''}</Tag>;
    return <Tag color="success">完成 · {r.result_count} 张</Tag>;
  };

  // 「生成记录」区: 批次列表 → 展开候选缩略图 → 每张 provider + 选入套系 + 删除。
  const renderRunsSection = () => {
    const items = runs.map((r) => {
      const cands = candByRun[r.run_id] ?? [];
      const loadingCands = candLoading[r.run_id];
      return {
        key: String(r.run_id),
        label: (
          <Space size={8} wrap>
            <Text strong>#{r.run_id}</Text>
            {engineTag(r.engine)}
            {runStatusTag(r)}
            <Text type="secondary" style={{ maxWidth: 320, display: 'inline-block' }} ellipsis>
              {r.prompt || '(无 prompt)'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
            </Text>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              title="复制 prompt"
              onClick={(e) => { e.stopPropagation(); copyPrompt(r.prompt); }}
            />
            {r.status === 'failed' && (
              <Popconfirm
                title="删除该失败批次?"
                okText="删除"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleDeleteRun(r.run_id)}
                onPopupClick={(e) => e.stopPropagation()}
              >
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  title="删除失败批次"
                  onClick={(e) => e.stopPropagation()}
                />
              </Popconfirm>
            )}
          </Space>
        ),
        children: loadingCands ? (
          <Spin />
        ) : cands.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              r.status === 'running'
                ? '生成中，稍后刷新查看候选'
                : '无候选 (可能已全部选入/删除, 或本批全部失败)'
            }
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {cands.map((c) => (
              <div
                key={c.plate_id}
                style={{
                  width: 150,
                  border: '1px solid #eee',
                  borderRadius: 6,
                  padding: 6,
                  textAlign: 'center',
                }}
              >
                <AntImage
                  src={c.plate_image_url}
                  alt={`cand-${c.plate_id}`}
                  width="100%"
                  height={180}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                  preview={{ mask: '查看大图' }}
                />
                <div style={{ fontSize: 11, color: '#999', margin: '4px 0', wordBreak: 'break-all' }}>
                  {c.provider || '—'}
                </div>
                <Space size={4}>
                  <Button
                    size="small"
                    type="primary"
                    loading={selectingId === c.plate_id}
                    onClick={() => handleSelectCandidate(r.run_id, c.plate_id)}
                  >
                    选入套系
                  </Button>
                  <Button
                    size="small"
                    type="link"
                    icon={<SwapOutlined />}
                    title="转移到其他套系"
                    onClick={() => openTransfer(r.run_id, c.plate_id)}
                  >
                    转移
                  </Button>
                  <Popconfirm
                    title="删除该候选?"
                    okText="删除"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDeleteCandidate(r.run_id, c)}
                  >
                    <Button size="small" danger type="link">
                      删除
                    </Button>
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </div>
        ),
      };
    });

    return (
      <Card
        title="生成记录 (批次候选)"
        size="small"
        style={{ marginBottom: 16 }}
        extra={
          <Button size="small" icon={<ReloadOutlined />} onClick={loadRuns} loading={runsLoading}>
            刷新记录
          </Button>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="每次「生成底版」= 一个批次。候选不进标框工作区; 展开批次挑选缩略图「选入套系」后才转 draft 进入下方标框。每张标注实际出图 provider (含降级)。"
        />
        {runs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无生成记录 — 点右上「生成底版」发起一次"
          />
        ) : (
          <Collapse items={items} onChange={onRunsCollapseChange} />
        )}
      </Card>
    );
  };

  const faceColumns = [
    {
      title: 'order',
      dataIndex: 'order',
      width: 88,
      render: (_: unknown, _f: PlateFace, idx: number) => (
        <InputNumber
          size="small"
          min={1}
          value={faces[idx].order}
          onChange={(v) => updateFace(idx, { order: typeof v === 'number' ? v : 1 })}
          style={{ width: 64 }}
        />
      ),
    },
    {
      title: 'role',
      dataIndex: 'role',
      render: (_: unknown, _f: PlateFace, idx: number) => (
        <Input
          size="small"
          placeholder="角色(可选)"
          value={faces[idx].role}
          onChange={(e) => updateFace(idx, { role: e.target.value })}
        />
      ),
    },
    {
      title: 'gender',
      dataIndex: 'gender',
      width: 110,
      render: (_: unknown, _f: PlateFace, idx: number) => (
        <Select
          size="small"
          allowClear
          placeholder="不限"
          value={faces[idx].gender || undefined}
          onChange={(v) => updateFace(idx, { gender: v ?? '' })}
          options={[
            { value: 'male', label: '男' },
            { value: 'female', label: '女' },
          ]}
          style={{ width: 90 }}
        />
      ),
    },
    {
      title: 'bbox',
      render: (_: unknown, _f: PlateFace, idx: number) => {
        const b = faces[idx].bbox;
        return (
          <Text type="secondary" style={{ fontSize: 11 }}>
            {b.x.toFixed(2)},{b.y.toFixed(2)} · {b.w.toFixed(2)}×{b.h.toFixed(2)}
          </Text>
        );
      },
    },
    {
      title: '',
      width: 60,
      render: (_: unknown, _f: PlateFace, idx: number) => (
        <Popconfirm title="删除该框?" onConfirm={() => removeFace(idx)}>
          <Button size="small" danger type="link">
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/content/${id}`)}>
          返回套系
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            load();
            loadRuns();
          }}
        >
          刷新
        </Button>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={() => setAutogenOpen(true)}
        >
          生成底版
        </Button>
      </Space>

      {renderRunsSection()}

      {loading ? (
        <Skeleton active />
      ) : (
        <Card
          title={`底版管理（生成候选 / 标框 / 上架）— ${pkg?.name ?? `#${id}`}`}
          extra={pkg && <Tag color="blue">app#{pkg.app_id}</Tag>}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="在底版图上拖拽画出每个人脸位置的框；框只是元数据 (归一化坐标)，不会烤进底版图。换脸按 order 逐框单脸换 (photo#i → order=i 的框)。"
          />

          {plates.length === 0 ? (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Alert
                type="warning"
                showIcon
                message="该套系暂无底版 (plate)。创想熊导入的套系底版存在「样片」里，点下方按钮可导入为可标框的底版。"
              />
              <Button type="primary" loading={importing} onClick={handleImportFromSamples}>
                从套系现有底版导入
              </Button>
              <Typography.Text type="secondary">
                导入后在底版图上标框（创想熊底版的框是烤进图里的，按图中位置画框对齐即可；长期用自产无框底版替换）。
              </Typography.Text>
            </Space>
          ) : (
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* 左: plate 列表 */}
              <div style={{ width: 150, flexShrink: 0 }}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  {plates.map((p) => (
                    <div
                      key={p.plate_id}
                      onClick={() => selectPlate(p.plate_id)}
                      style={{
                        border:
                          p.plate_id === selectedId ? '2px solid #4f46e5' : '1px solid #eee',
                        borderRadius: 6,
                        padding: 4,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      <img
                        src={p.plate_image_url}
                        alt={`plate-${p.plate_id}`}
                        style={{ width: '100%', height: 110, objectFit: 'cover', borderRadius: 4 }}
                      />
                      <div style={{ fontSize: 11, marginTop: 2 }}>
                        <Tag
                          color={p.status === 'active' ? 'green' : p.status === 'draft' ? 'orange' : 'default'}
                          style={{ marginRight: 0 }}
                        >
                          {p.status}
                        </Tag>
                      </div>
                      <div style={{ fontSize: 11, color: '#999' }}>{p.faces.length} 框</div>
                      <Popconfirm
                        title="删除该底版?"
                        description="从列表移除(归档), 不可在此页恢复"
                        okText="删除"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleArchive(p.plate_id, p.faces)}
                      >
                        <Button
                          size="small"
                          danger
                          type="link"
                          style={{ fontSize: 11, padding: 0, height: 'auto' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          删除
                        </Button>
                      </Popconfirm>
                    </div>
                  ))}
                </Space>
              </div>

              {/* 中: 画框区 */}
              <div style={{ flex: '1 1 380px', minWidth: 320 }}>{renderOverlay()}</div>

              {/* 右: faces 编辑 */}
              <div style={{ flex: '1 1 360px', minWidth: 320 }}>
                <Space style={{ marginBottom: 12 }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={!selectedPlate}
                    onClick={() => onSave(false)}
                  >
                    保存标框
                  </Button>
                  <Button
                    loading={saving}
                    disabled={!selectedPlate}
                    onClick={() => {
                      // 0 框可上架: 无框走 ReActor 自动检测单脸换脸 (cxx 导入底版
                      // 即此模式, onething client 按 len(PlateFaces) 分发)。多人底版
                      // 必须标框 (逐框映射用户脸), 故 0 框时二次确认防漏标。
                      if (faces.length === 0 && !window.confirm('未标框:单人底版将用自动检测换脸(可用);多人底版必须标框否则脸位映射不可控。确认直接上架?')) return;
                      onSave(true);
                    }}
                  >
                    保存并 → active
                  </Button>
                  {dirty && <Tag color="orange">未保存</Tag>}
                </Space>
                <Table
                  size="small"
                  rowKey={(_, idx) => String(idx)}
                  dataSource={faces}
                  columns={faceColumns}
                  pagination={false}
                  locale={{ emptyText: '尚无框 — 在左侧图上拖拽画框' }}
                  onRow={(_, idx) => ({
                    onClick: () => setActiveFace(idx ?? null),
                    style:
                      activeFace === (idx ?? -1) ? { background: '#eef2ff' } : undefined,
                  })}
                />
              </div>
            </div>
          )}
        </Card>
      )}

      <Modal
        title="生成底版"
        open={autogenOpen}
        onOk={handleAutogen}
        onCancel={() => setAutogenOpen(false)}
        okText="提交生成"
        confirmLoading={autogenSubmitting}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="填 prompt、选引擎、选张数，一键生成一批干净底版候选。生成异步，候选落到上方「生成记录」批次，展开挑选「选入套系」后才进标框工作区。"
        />
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text type="secondary">底版画面 prompt（txt2img，无脸）</Text>
            <Input.TextArea
              rows={4}
              value={agPrompt}
              onChange={(e) => setAgPrompt(e.target.value)}
              placeholder="例：中秋国风庭院，红灯笼与满月，暖色调，写实摄影"
            />
          </div>
          <Space size={24} wrap>
            <span>
              <Text type="secondary" style={{ marginRight: 8 }}>
                引擎
              </Text>
              <Select
                value={agEngine}
                onChange={(v) => setAgEngine(v)}
                style={{ width: 160 }}
                options={[
                  { value: 'jimeng', label: '即梦（国风优先）' },
                  { value: 'onething', label: 'onething（默认链）' },
                ]}
              />
            </span>
            <span>
              <Text type="secondary" style={{ marginRight: 8 }}>
                张数
              </Text>
              <InputNumber
                min={1}
                max={12}
                value={agCount}
                onChange={(v) => setAgCount(typeof v === 'number' ? v : 4)}
                style={{ width: 100 }}
              />
            </span>
          </Space>
        </Space>
      </Modal>

      {/* 转移候选到其他套系 */}
      <Modal
        title="转移候选到其他套系"
        open={!!transferPlate}
        onOk={doTransfer}
        okText="转移"
        confirmLoading={transferring}
        okButtonProps={{ disabled: !transferTarget }}
        onCancel={() => setTransferPlate(null)}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text type="secondary">转移后照片成为目标套系的候选, 在「内容生产→照片库」筛该套系「选入」后, 到其底版页标框/上架。</Text>
          <Select
            style={{ width: '100%' }}
            showSearch
            filterOption={false}
            onSearch={onPkgSearch}
            loading={pkgSearching}
            notFoundContent={pkgSearching ? '搜索中…' : '无匹配套系'}
            placeholder="输入套系名搜索"
            options={pkgOptions}
            value={transferTarget}
            onChange={setTransferTarget}
          />
        </Space>
      </Modal>
    </div>
  );
}
